import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Container, Collapse } from 'react-bootstrap';
import _ from 'lodash';
import { AccountIcon } from './icons/AccountIcon';
import { CommunicationIcon } from './icons/CommunicationIcon';
import { ConnectedSitesIcon } from './icons/ConnectedSitesIcon';
import { FinancesIcon } from './icons/FinancesIcon';
import { MembersIcon } from './icons/MembersIcon';
import { DashboardIcon } from './icons/DashboardIcon';
import { EventsIcon } from './icons/EventsIcon';
import { FormsIcon } from './icons/FormsIcon';
import { ReportsIcon } from './icons/ReportsIcons';
import { SettingsIcon } from './icons/SettingsIcon';
import OrganizationMenu from './organizationMenu/OrganizationMenu';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getSecuritySection, SecurityModel, SecuritySections } from '../../../models/security/security';

export interface Nav {
  id: string;
  title: string;
  to?: string;
  visible: boolean;
  submenus: Submenu[] | null;
  planId: number;
  icon: unknown;
  rights: SecuritySections | null;
}

export interface Submenu {
  id: string;
  title: string;
  to: string;
  rights: SecuritySections | null;
}

export const DefaultPage = '/registrations';
export const FallbackPage = '/organization-selection';

const mockedNav: Nav[] = [
  {
    id: 'user-id',
    title: 'My Account',
    to: '/account',
    visible: false,
    submenus: null,
    planId: 3,
    icon: <AccountIcon fill="#FFFFFF" stroke="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  // {
  //   id: 'organization-id',
  //   title: '',
  //   to: '/organizations',
  //   visible: false,
  //   submenus: [
  //     {
  //       title: 'Organizations',
  //       choices: ['Org 1', 'Org 2', 'Org 3'],
  //     },
  //   ],
  //   planId: 3,
  //   icon: <MembersIcon fill="#FFFFFF" width={20} height={20} />,
  //   right: null,
  // },
  {
    id: 'home-id',
    title: 'navbar.home.title',
    to: '/organization-selection',
    visible: true,
    submenus: null,
    planId: 3,
    icon: <DashboardIcon fill="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  {
    id: 'members-id',
    title: 'Members',
    to: '/members',
    visible: false,
    submenus: null,
    planId: 3,
    icon: <MembersIcon fill="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  {
    id: 'events-id',
    title: 'Events',
    to: '/events',
    visible: false,
    submenus: null,
    planId: 3,
    icon: <EventsIcon fill="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  {
    id: 'forms-id',
    title: 'navbar.forms.title',
    to: '/registrations',
    visible: true,
    submenus: null,
    planId: 3,
    icon: <FormsIcon fill="#FFFFFF" width={20} height={20} />,
    rights: SecuritySections.FORM,
  },
  {
    id: 'finances-id',
    title: 'Finances',
    to: '/finances',
    visible: false,
    submenus: null,
    planId: 3,
    icon: <FinancesIcon fill="#FFFFFF" stroke="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  {
    id: 'connected-sites-id',
    title: 'Connected Sites',
    to: '/connectedsites',
    visible: false,
    submenus: null,
    planId: 3,
    icon: <ConnectedSitesIcon fill="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  {
    id: 'Communication-id',
    title: 'Communication',
    to: '/communication',
    visible: false,
    submenus: null,
    planId: 3,
    icon: <CommunicationIcon fill="#FFFFFF" width={20} height={20} />,
    rights: null,
  },
  {
    id: 'reports-id',
    title: 'navbar.settings.reports.title',
    to: '/reports',
    visible: true,
    submenus: null,
    planId: 3,
    icon: <ReportsIcon fill="#FFFFFF" width={20} height={20} />,
    rights: SecuritySections.REPORT,
  },
  {
    id: 'settings-id',
    title: 'navbar.settings.title',
    visible: true,
    submenus: [
      {
        title: 'navbar.settings.paymentOptions.title',
        rights: SecuritySections.PAYMENT_ACCOUNTS,
        id: 'payment-options-id',
        to: '/payment-accounts',
      },
      {
        title: 'navbar.settings.users.title',
        rights: SecuritySections.USERS,
        id: 'users-id',
        to: '/users',
      },
      {
        id: 'organization-options-id',
        title: 'navbar.settings.organization.title',
        to: '/organization',
        rights: SecuritySections.ORGANIZATION,
      },
    ],
    planId: 3,
    icon: <SettingsIcon fill="#FFFFFF" width={20} height={20} />,
    rights: SecuritySections.ORGANIZATION,
  },
];

const Navbar: FC = () => {
  const [activePage, setActivePage] = useState<any>({});
  const [openedNav, setOpenedNav] = useState<Nav[]>([]);
  const [securityState, setSecurityState] = useState<SecurityModel[] | null>(null);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const history = useHistory();

  useEffect(() => {
    if (security) {
      setSecurityState(security);
    }
  }, [security]);

  useEffect(() => {
    const activePage = {};
    mockedNav.forEach((item) => {
      if (item.to && history.location.pathname.startsWith(item.to)) {
        activePage[item.id] = true;
      }

      if (item.submenus) {
        item.submenus.forEach((subItem) => {
          if (history.location.pathname.startsWith(subItem.to)) {
            activePage[subItem.id] = true;

            if (!_.find(openedNav, (nav) => nav.id == item.id)) {
              setOpenedNav([...openedNav, item]);
            }
          }
        });
      }
    });
    setActivePage(activePage);
  }, [history.location]);

  const checkSectionRights = (section: SecuritySections | null): boolean => {
    if (securityState && section) {
      return getSecuritySection(securityState, section)?.read ?? true;
    }
    //if section's right = null, no need to check security (e.g. dashboard) and return true to render icon in navbar
    return true;
  };

  const handleNavigation = (navItem) => {
    if (navItem.submenus && navItem.submenus.length > 0) {
      const navOpened = _.find(openedNav, (nav) => nav.id == navItem.id);
      if (navOpened) {
        setOpenedNav(_.filter(openedNav, (nav) => nav.id != navItem.id));
      } else {
        setOpenedNav([...openedNav, navItem]);
      }
    } else {
      setActivePage(navItem.to);
      history.push(navItem.to);
    }
  };

  const showNavItem = (item) => {
    if (item.visible && checkSectionRights(item.rights)) {
      const opened = _.find(openedNav, (nav) => nav.id == item.id);
      return (
        <div
          key={`nav-menu-item-${item.id}`}
          className={`nav-item-container`}
          data-after-content={intl.formatMessage({ id: item.title })}
        >
          <div className={`nav-item dropdown ${activePage[item.id] ? 'active' : ''}`}>
            <div
              className="nav-link d-flex flex-row justify-content-between align-items-center"
              onClick={() => handleNavigation(item)}
            >
              <div className="nav-title">
                <span className="me-3">{item.icon}</span>
                <span>{intl.formatMessage({ id: item.title })}</span>
              </div>
              {item.submenus && item.submenus.length > 0 && opened && <span className="mdi mdi-chevron-up"></span>}
              {item.submenus && item.submenus.length > 0 && !opened && <span className="mdi mdi-chevron-down"></span>}
            </div>
          </div>

          {item.submenus && item.submenus.length > 0 && showSubmenus(item.submenus, !!opened)}
        </div>
      );
    }
  };

  const showSubmenus = (submenus, opened) => {
    return (
      <Collapse in={opened}>
        <div className={`nav-item-container`}>
          {submenus.map((item) => {
            return (
              <div
                key={`nav-submenu-item-${item.id}`}
                className={`nav-submenu-item nav-item ${activePage[item.id] ? 'active' : ''}`}
              >
                <div className="nav-link" onClick={() => handleNavigation(item)}>
                  <div className="nav-title d-flex align-items-center">
                    <span>{intl.formatMessage({ id: item.title })}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Collapse>
    );
  };

  return (
    <>
      <Container className="navbar">
        <nav className={`nav`}>
          <div className="navbar-menu">
            <div className="navbar-header">
              <OrganizationMenu setActivePage={setActivePage}></OrganizationMenu>
            </div>
            <div className="navbar-items">
              <ul>{_.map(mockedNav, (item) => showNavItem(item))}</ul>
            </div>
          </div>
        </nav>
      </Container>
    </>
  );
};

export default Navbar;
